import { PaymentMethod } from '@aaa-ncnu-ie/mypolicy-api-billing';
import { Box, Button, Card, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import { useOTSPQuery } from '../../api/oneTimeScheduledPaymentInfo';
import Add from '../../assets/icons/Add';
import WalletSelection from '../../components/WalletSelection/WalletSelection';
import StyledButton from '../../components/styledComponents/Button';
import StatusAlert from '../../components/styledComponents/StatusAlert';
import { EVALUE_DISCOUNT_REMOVAL } from '../../constants/alertMessages';
import colors from '../../constants/colors';
import {
  EValueStatus,
  PaymentAmountTypeLabels,
  StatusType,
} from '../../constants/enums';
import useAnalytics from '../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../store/SnackbarProvider';
import { usePaymentsContext } from '../../store/payments';

const PaymentMethodPage = () => {
  const location = useLocation();
  const isEdit = location.state?.edit;
  const { closeSnackbar } = useSnackbarsContext();
  const { state } = usePaymentsContext();
  const isSinglePayment = !isEmpty(state.singleSelectedPayment);
  const isPayMultiplePolicies = !isEmpty(state.selectedPayments);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod>('' as PaymentMethod);
  const [selectedPaymentAccountToken, setSelectedPaymentAccountToken] =
    useState('');
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const { data } = useWelcomeQuery();
  const { data: otspData } = useOTSPQuery(data?.policies);
  const scheduledPayment =
    otspData?.[state.selectedBill.policyNumber]?.schedulePaymentDetails; // OTSP for the current policy
  const showEValueWarning =
    selectedPaymentMethod &&
    [EValueStatus.ACTIVE, EValueStatus.PENDING].includes(
      location.state?.eValue?.toUpperCase() as EValueStatus
    ) &&
    selectedPaymentMethod !== 'EFT';

  const paymentOptionSelected = isSinglePayment || isPayMultiplePolicies;

  const buttonText = (() => {
    if (showEValueWarning) return 'Continue and remove discount';
    if (isEdit) return 'Save';

    return 'Continue';
  })();

  const handleClick = async () => {
    let analyticsProps = {};
    if (isSinglePayment) {
      analyticsProps = {
        policy_number: state.singleSelectedPayment.policyNumber,
        state: state.singleSelectedPayment.riskState,
        policy_type: state.singleSelectedPayment.policyType,
      };
    } else {
      const policyNumbers = Object.keys(state.selectedPayments).filter(
        (policyNumber) =>
          Number(state.selectedPayments[policyNumber].amount) > 0
      );
      const totalPayment = policyNumbers.reduce(
        (acc, policyNumber) =>
          acc + Number(state.selectedPayments[policyNumber].amount),
        0
      );
      analyticsProps = { total_payment_amount: totalPayment.toFixed(2) };
    }
    track('Payment Managed', {
      event_type: 'User Information Entered',
      location: 'Feature Card',
      account_type: state.selectedPaymentMethod?.analyticsAccountType,
      payment_amount: state.singleSelectedPayment.amount,
      payment_selection:
        PaymentAmountTypeLabels[state.singleSelectedPayment.paymentType],
      payment_type: isSinglePayment ? 'One Policy' : 'Multiple Policies',
      ...analyticsProps,
    });
    navigate('/payment/review');
  };

  return (
    <Stack
      direction="column"
      data-testid="paymentMethodPage"
      sx={{
        width: 1,
        minHeight: '100%',
        backgroundColor: colors.csaaGray,
        px: { mobile: 2, desktop: 19.5 },
        pb: 3,
        pt: { mobile: 2, tablet: 3, desktop: 9 },
      }}
    >
      {!paymentOptionSelected && <Navigate to="/bills" replace />}
      <Typography
        variant="h1"
        sx={{
          fontWeight: { mobile: 400, tablet: 500 },
          fontSize: { mobile: 33, tablet: 44 },
          pb: { mobile: 0.5, tablet: 1 },
          lineHeight: { mobile: '32px', tablet: '56px' },
        }}
      >
        How would you like to pay?
      </Typography>
      <Card
        component={Stack}
        spacing={2}
        sx={{
          borderRadius: 2.5,
          mt: 3,
          px: {
            mobile: 2,
            tablet: 3,
            desktop: 5,
          },
          py: {
            mobile: 2,
            tablet: 3,
            desktop: 5,
          },
          height: 'auto',
          boxShadow: '0px 4px 8px 0px rgba(25, 30, 43, 0.08)',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 28 }}>
          Payment method
        </Typography>
        <StatusAlert
          open={
            !!(scheduledPayment?.paymentAmount && scheduledPayment?.paymentDate)
          }
          text={`There is already a one-time payment of ${scheduledPayment?.paymentAmount} that will process 
          on ${scheduledPayment?.paymentDate}. If your scheduled payment is processed before your AutoPay date, 
          your automatic payment may be reduced by the amount of the scheduled payment.`}
          status={StatusType.WARNING}
        />
        {showEValueWarning && (
          <Box sx={{ mt: 3 }}>
            <StatusAlert
              text={
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '18px',
                    fontFamily: 'Cabin',
                  }}
                >
                  {EVALUE_DISCOUNT_REMOVAL}
                </Typography>
              }
              open={showEValueWarning}
              status={StatusType.WARNING}
            />
          </Box>
        )}
        <Stack direction="column">
          <Stack
            direction="row"
            sx={{
              justifyContent: { mobile: 'center', tablet: 'flex-end' }
            }}
          >
            <Button
              component={Link}
              to="add"
              startIcon={<Add />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 18,
              }}
              onClick={() => {
                track('Payment Methods Accessed', {
                  event_type: 'Link Accessed',
                  location: 'Feature Card',
                  link: 'Add payment method',
                  policy_number: state.selectedBill.policyNumber,
                  state: state.selectedBill.riskState,
                  policy_type: state.selectedBill.policyType,
                });
              }}
              disableRipple
            >
              Add payment method
            </Button>
          </Stack>
        </Stack>
        <WalletSelection
          setSelectedPaymentType={setSelectedPaymentMethod}
          setSelectedPaymentAccountToken={setSelectedPaymentAccountToken}
          selectedPaymentAccountToken={selectedPaymentAccountToken}
        />
        <Stack
          direction={{ mobile: 'column', tablet: 'row' }}
          spacing={{ mobile: 1, tablet: 2 }}
          sx={{
            justifyContent: "flex-end",
            mt: 3
          }}>
          <StyledButton
            data-testid="paymentMethodSelectCancel"
            component={Link}
            to={isSinglePayment ? '/payment' : '/multiple-payment'}
            replace
            variant="outlined"
            color="inherit"
            sx={{
              width: { mobile: 1, tablet: 'auto' },
              '&:hover': {
                borderRadius: 1,
                borderWidth: 2,
              },
            }}
            onClick={() => {
              closeSnackbar();
            }}
          >
            Cancel
          </StyledButton>
          <StyledButton
            data-testid="paymentMethodSelectContinue"
            disabled={!selectedPaymentAccountToken}
            onClick={handleClick}
          >
            {buttonText}
          </StyledButton>
        </Stack>
      </Card>
    </Stack>
  );
};

export default PaymentMethodPage;
